// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Per-member row actions for the providers roster: resend invite (pending only),
// change role, and remove (cancel pending / offboard active). All org_admin-gated
// server-side and keyed by the stable member id. Uses a Radix Popover so the menu
// portals out of the list card (no clipping) with built-in outside-click/escape.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Popover from '@radix-ui/react-popover'
import { MoreVertical, Send, Trash2, Check, Loader2 } from 'lucide-react'
import { ApiError, resendInvite, changeMemberRole, removeMember, type OrgMember } from '@/lib/api'

const ROLES = ['provider', 'clinic_staff', 'org_admin'] as const

export interface ActionResult {
  kind: 'success' | 'error'
  text: string
}

interface Props {
  member: { id: string; role: string; status: string }
  onChanged: () => void
  onResult: (r: ActionResult) => void
}

export default function MemberActions({ member, onChanged, onResult }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  function errorText(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.code === 'last_org_admin') return t('providers.errLastAdmin')
      if (err.code === 'not_pending') return t('providers.errNotPending')
      if (err.code === 'member_not_found') return t('providers.errNotFound')
    }
    return t('providers.actionError')
  }

  async function run(fn: () => Promise<OrgMember>, successText: string) {
    setBusy(true)
    try {
      await fn()
      onResult({ kind: 'success', text: successText })
      onChanged()
      setOpen(false)
    } catch (err) {
      onResult({ kind: 'error', text: errorText(err) })
      setOpen(false)
    } finally {
      setBusy(false)
      setConfirmingRemove(false)
    }
  }

  const itemCls =
    'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-default text-left'

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setConfirmingRemove(false)
      }}
    >
      <Popover.Trigger asChild>
        <button
          aria-label={t('providers.memberActions')}
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          className="z-50 w-56 rounded-xl border border-border bg-card shadow-xl p-1.5 animate-in fade-in zoom-in-95"
        >
          {member.status === 'invited' && (
            <button disabled={busy} className={itemCls} onClick={() => void run(() => resendInvite(member.id), t('providers.resent'))}>
              <Send className="h-4 w-4 text-muted-foreground" />
              {t('providers.resendInvite')}
            </button>
          )}

          <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('providers.roleLabel')}
          </div>
          {ROLES.map((r) => (
            <button
              key={r}
              disabled={busy || member.role === r}
              className={`${itemCls} justify-between`}
              onClick={() => void run(() => changeMemberRole(member.id, r), t('providers.roleUpdated'))}
            >
              {t(`providers.role_${r}`)}
              {member.role === r && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}

          <div className="h-px bg-border my-1.5" />

          {confirmingRemove ? (
            <div className="px-2 py-1.5 flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">{t('providers.removeConfirm')}</p>
              <div className="flex gap-2">
                <button
                  className="flex-1 h-8 rounded-lg text-xs font-medium border border-border hover:bg-secondary transition-colors"
                  onClick={() => setConfirmingRemove(false)}
                >
                  {t('providers.cancel')}
                </button>
                <button
                  disabled={busy}
                  className="flex-1 h-8 rounded-lg text-xs font-semibold bg-destructive text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                  onClick={() => void run(() => removeMember(member.id), t('providers.removed'))}
                >
                  {t('providers.removeMember')}
                </button>
              </div>
            </div>
          ) : (
            <button className={`${itemCls} text-destructive`} onClick={() => setConfirmingRemove(true)}>
              <Trash2 className="h-4 w-4" />
              {t('providers.removeMember')}
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
